import React, { useEffect, useRef, useState } from "react"
import Popup from 'reactjs-popup'
import PropTypes from 'prop-types'
import Button from 'react-bootstrap/Button'
import Cookies from 'js-cookie'
import { jwtDecode } from 'jwt-decode'
import { HubConnectionBuilder } from "@microsoft/signalr"
import { v4 as uuidv4 } from 'uuid'

import { GetLanguageId, SetLanguageId, ResetLanguageId, Translate } from "./Translator"
import { LookupApiGetSearchHistory, LookupApiUpdateSearchHistory, LookupApiGetSpeedDials, LookupApiUpdateSpeedDials, LookupApiClientReplicatorInfo, LookupApiLookupNumber, LookupApiLookupMultipleNumbers } from "../ExternalApi/LookupApi"
import { PhoneApiGetPhoneSettings, PhoneApiSetPhoneSettings, PhoneApiSubscribe, PhoneApiMakeCall } from "../ExternalApi/PhoneApi"
import { PushApiRegisterUserAsWebclientUser, PushApiSetBrowserNotificationSetting, PushApiAddBrowserNotification, PushApiGetBrowserNotificationSetting, PushApiDeleteBrowserNotification, PushApiGetCallHistory, PushApiCallHistoryDeleteCall, PushApiCallHistoryAddOutboundCall } from "../ExternalApi/PushApi"
import { SigninApiGetTokenGntel, SigninApiGetUserSetting } from "../ExternalApi/SigninApi"

import { NowInDateTimeFormat, IsEmptyString, UrlBase64ToUint8Array, GetCallHistory, GetCallHistoryFromLocalStorage, GetHistoryCallRecognition, GetPropertyByValue, RemoveServiceWorker, GetRecognitionFieldTypeValue } from "./GeneralFunctions"
import { GntelRefreshTokens, GntelTokenIsExpiredOrExpiresSoon, GntelAccessToken, GntelRefreshToken, GntelJsonRpcApiUrl } from "./Gntel"

import EmbeddedHubspot from "../Embedded/Hubspot/EmbeddedHubspot"
import EmbeddedSalesforce from "../Embedded/Salesforce/EmbeddedSalesforce"
import EmbeddedZendesk from "../Embedded/Zendesk/EmbeddedZendesk"
import EmbeddedZoho from "../Embedded/Zoho/EmbeddedZoho"
import EmbeddedDynamics from "../Embedded/Dynamics/EmbeddedDynamics"

import CallNotification from "./CallNotification"
import UserSettingsModal from "./UserSettingsModal"
import CallHistory from "./CallHistory"
import SearchArea from "./SearchArea"
import SpeedDials from "./SpeedDials"

import LogoEmptyPng from '../Assets/LogoEmpty.png'
import CloudCTIPng from '../Assets/CloudCTI.png'
import EnreachPng from '../Assets/Enreach.png'
import SummaPng from '../Assets/Summa.png'
import AccountPng from '../Assets/account.png'
import SettingsUserPng from '../Assets/settings_user.png'
import SignOutPng from '../Assets/signout.png'

import "../css/Main.css"

//Subclasses

const Main = (props) => {
  const mRef = React.createRef()
  
  //Push
  const cPushPublicVapidKey = "BJovlKvWRuqeCSbyyogJUuJZ3iOPlwF1JagAADr3E7zqtNb-c-oKm1MB3eptSuOuoNWlVfkBDgiPriKBU0N7ifI"
  
  const [mResetter, SetResetter] = useState("")

  const [mRepainter, SetRepainter] = useState(false)

  const mSecurityToken = useRef(localStorage.getItem("securitytoken"))
  const [mSecurityTokenData, SetSecurityTokenData] = useState({})

  const [mVisualMode, SetVisualMode] = useState("FullMode")
  const [mMobileModeWindow, SetMobileModeWindow] = useState(localStorage.getItem("MobileModeWindow") || "Search")

  const [mSearchHistory, SetSearchHistory] = useState([])
  const [mSpeedDials, SetSpeedDials] = useState([])

  const [mPhoneApiError, SetPhoneApiError] = useState(null)

  const [mPhoneSettings, SetPhoneSettings] = useState(null)
  const [mPhoneSettingsTemplate, SetPhoneSettingsTemplate] = useState("")

  const [mLastCallEventId, SetLastCallEventId] = useState("") //updates on every call event. for embedded repaint purposed

  const [mIntegrations, SetIntegrations] = useState([])
  const [mCallfieldScripts, SetCallfieldScripts] = useState([])

  const [mShowModalUserSettings, SetShowModalUserSettings] = useState(false)
  const [mShowModalUserPhoneSettings, SetShowModalUserPhoneSettings] = useState(false)
  const [mShowModalAccount, SetShowModalAccount] = useState(false)

  const [mSignalRConnection, SetSignalRConnection] = useState(new HubConnectionBuilder().withUrl("https://" + window.location.hostname + "/eventhub/api").withAutomaticReconnect([0,2000,10000,30000,60000,60000,300000,300000,300000,300000,300000,300000,300000,300000,300000,300000,300000,300000]).build()); 
  //const [mSignalRConnection, SetSignalRConnection] = useState(new HubConnectionBuilder().withUrl((props.ProductName === "Voxone" ? "https://webcrm.voxone.co.uk" : "https://webclient2.0.cloudcti.nl") + "/eventhub/api").withAutomaticReconnect([0,2000,10000,30000,60000,60000,300000,300000,300000,300000,300000,300000,300000,300000,300000,300000,300000,300000]).build()); 
  //const [mSignalRConnection, SetSignalRConnection] = useState(new HubConnectionBuilder().withUrl("https://" + window.location.hostname + "/eventhub/api").withAutomaticReconnect([0,2000,10000,30000,60000,60000,300000,300000,300000,300000,300000,300000,300000,300000,300000,300000,300000,300000]).build()); 

  const [mBrowserNotificationsActive, SetBrowserNotificationsActive] = useState(false)

  const [mRecognitionCache,SetRecognitionCache] = useState({}) //number=recognition
  const [mRecognitionCacheQueryingNumbers,SetRecognitionCacheQueryingNumbers] = useState([])

  const [mBroadcastChannel, SetBroadcastChannel] = useState(new BroadcastChannel("cloudctiwebclient2024"))


  // Embedded specific, ignored for webclient
  //Is this tab the primary tab for the current call
  const [mPrimaryTab, SetPrimaryTab] = useState(false)  

  useEffect(() => {

    const handleResize = () => {
      SetVisualMode(GetVisualMode())
    }

    const HandleBrowserNotifications = async () => {

      //const cServiceWorkerVersion = "1"
      //if (localStorage.getItem("ServiceWorkerVersion") !== cServiceWorkerVersion)
      //{
        await RemoveServiceWorker()
        if ('serviceWorker' in navigator) 
        {
          //Start serviceworker for push. This does not enable push messages by itself
          if ('PushManager' in window) {
            await navigator.serviceWorker.register(`${process.env.PUBLIC_URL}/swp.js`)
            //localStorage.setItem("ServiceWorkerVersion",cServiceWorkerVersion)
            console.info('Service worker was registered.');
          } else {
            console.error('Browser does not support service workers or push messages.');
          }

        }
      //}

      const BrowserNotificationSettingResult = await PushApiGetBrowserNotificationSetting(mSecurityToken.current)
      ProcessPushApiGetBrowserNotificationSettingResult(BrowserNotificationSettingResult)
    }

    UpdateSecurityToken().then(() => {

      if (IsEmptyString(mSecurityToken.current))
      {
        SignOut()
        return
      }

      const DecodedToken = jwtDecode(mSecurityToken.current)
      SetSecurityTokenData({ 
        Email : DecodedToken.Email,
        UserName : DecodedToken.UserName,
        TenantName : DecodedToken.TenantName,
        LicenceName : DecodedToken.LicenceName,
        LicencePlatforms : DecodedToken.LicencePlatforms,
        UserIsAdmin : DecodedToken.UserIsAdmin,
        UserHasRecognitionAndScreenpopLicense : DecodedToken.RecognitionAndScreenpop === "1",
        BrandLogoUrl : DecodedToken.BrandLogoUrl}
      )

      if (mSignalRConnection) {
        mSignalRConnection
          .start()
          .then(() => {
            mSignalRConnection.onclose(() => { OnSignalRError("Connection closed"); })
          })
          .then(() => {
            mSignalRConnection.onreconnected(() => { mSignalRConnection.invoke("Connect",DecodedToken.Email) })
          })
          .then(() => {
            mSignalRConnection.on("ReceiveMessage", (message) => {
              OnSignalREvent(message);
            });
          })
          .then(() => {
            mSignalRConnection.invoke("Connect",DecodedToken.Email)
          })
          .catch((error) => OnSignalRError(error));
      }

      let RecognitionCache = {}
      const RecognitionCacheString = localStorage.getItem("recognitioncache")
      if (!IsEmptyString(RecognitionCacheString))
      {
        try
        {
          RecognitionCache = JSON.parse(RecognitionCacheString)
        }
        catch
        {
          console.log("Error reading recognition cache. Clearing cache")
          localStorage.removeItem("recognitioncache")
        }

        if ((RecognitionCache !== undefined) && (RecognitionCache !== null))
        {       
          SetRecognitionCache(RecognitionCache)
        }
      }

      try
      {
        SigninApiGetUserSetting(mSecurityToken.current,"IntegrationOrder").then((result) => {
          localStorage.setItem("integrationorder",result ?? "")
        }) 
      }
      catch {}

      PushApiRegisterUserAsWebclientUser(mSecurityToken.current)

      LookupApiClientReplicatorInfo(mSecurityToken.current).then((clientreplicatorInfo) => { SetIntegrations(clientreplicatorInfo.data.Integrations); SetCallfieldScripts(clientreplicatorInfo.data.CallFieldScriptNames) })
    
      PushApiGetCallHistory(mSecurityToken.current).then((callHistoryResult) => { ProcessHistoryResult(callHistoryResult,RecognitionCache)})
      LookupApiGetSearchHistory(mSecurityToken.current).then((searchHistory) => { SetSearchHistory(searchHistory)})
      LookupApiGetSpeedDials(mSecurityToken.current).then((speedDials) => { SetSpeedDials(speedDials)})    

      PhoneApiSubscribe(mSecurityToken.current).then((result) => ProcessPhoneApiSubscribeResult(result))    

      ResetLanguageId()

      SetVisualMode(GetVisualMode())    

      mBroadcastChannel.addEventListener("message", function(event) {
        console.log("mBroadcastChannel event", event)
        if (event.data === "calllistchanged")
        {
          const timeout = setTimeout(() => {
            UpdateCallHistoryWithRecognitionCache(mRecognitionCache)
            const CallHistory = GetCallHistoryFromLocalStorage()

            if (ShouldUseGeneralLookupNumber())
            {
              let NumbersToBeQueried = Array.from(new Set(CallHistory.map(item => item.Number))).filter(item => mRecognitionCache[item] === undefined) //removes duplicates
              if (NumbersToBeQueried.length > 0)
              {
                console.log("Lookup multiple numbers",NumbersToBeQueried)
                LookupApiLookupMultipleNumbers(mSecurityToken.current, NumbersToBeQueried).then((result) => RecognitionCacheAddMultiple(result,NumbersToBeQueried,mRecognitionCache))
                SetRecognitionCacheQueryingNumbers(NumbersToBeQueried)
              }
            }

            SetResetter(uuidv4())
          }, 500)        
        }
        else if (event.data === "fullrepaint")
        {
          SetRepainter(!mRepainter)
        }
        else if (event.data === "signoutmessage") 
        {
          window.location.href = props.RedirectUrl
        }
      });

      navigator.registerProtocolHandler("tel", window.location.href + "?action=makecall&number=%s", "CloudCTI webclient")    

      if (IsEmptyString(props.Embedded))
      {
        HandleBrowserNotifications()
      }

      window.addEventListener('resize', handleResize);
      return () => { 
        window.removeEventListener('resize', handleResize)
        //try { document.body.removeChild(script) } catch {}
      } 
    })
  }, []); 

  const ProcessHistoryResult = (historyResult,recognitionCache) => {
    localStorage.setItem("callhistory",JSON.stringify(historyResult))
    SetRepainter(!mRepainter)

    UpdateCallHistoryWithRecognitionCache(recognitionCache)

    //This filters out the numbers in the recognitioncache that are no longer in the calllist
    const CleanedRecognitionCache = RecognitionCacheClean(recognitionCache,historyResult)

    let NumbersToBeQueried = Array.from(new Set(historyResult.map(item => item.Number))).filter(item => CleanedRecognitionCache[item] === undefined) //removes duplicates
    if (NumbersToBeQueried.length > 0)
    {
      console.log("Lookup multiple numbers",NumbersToBeQueried)
      LookupApiLookupMultipleNumbers(mSecurityToken.current, NumbersToBeQueried).then((result) => RecognitionCacheAddMultiple(result,NumbersToBeQueried,CleanedRecognitionCache))
      SetRecognitionCacheQueryingNumbers(NumbersToBeQueried)
    }
  }

  const UpdateCallHistoryWithRecognitionCache = (recognitionCache) => {
    let CallHistory = GetCallHistoryFromLocalStorage()

    CallHistory.map((call) => 
      {
        if (IsEmptyString(call.Name))
        {
          const RecognitionItem = recognitionCache[call.Number]
          if ((RecognitionItem !== undefined) && (RecognitionItem !== null))
          {
            call.Name = RecognitionItem.Name
            call.Organization = RecognitionItem.Organization
            call.Application = RecognitionItem.Application
            call.Scripts = RecognitionItem.Scripts
          }
        }
      })

    localStorage.setItem("callhistory",JSON.stringify(CallHistory))
  }

  const AddToSearchHistory = (aNumber, aName, aOrganization, aApplication, aScripts) => {
    try 
    {  
      const ExistingItems = mSearchHistory && (mSearchHistory.length > 0) ?  mSearchHistory
        .filter((item,index) => (item.Number !== aNumber))
        .filter((item,index) => (index < 9)) : []
      const SearchHistory = [{ Number: aNumber, Name:aName, Organization:aOrganization, Application:aApplication, Scripts:aScripts},...ExistingItems]
      LookupApiUpdateSearchHistory(mSecurityToken.current, SearchHistory)
        .then(() => LookupApiGetSearchHistory(mSecurityToken.current))
        .then((searchHistory) => { SetSearchHistory(searchHistory)})
    } 
    catch (ex) 
    {    
      console.log("AddToLastSearches failed",ex)
    }
  }

  const RemoveFromSearchHistory = (aIndex) => {
    try 
    {  
      const SearchHistory = mSearchHistory && (mSearchHistory.length > 0) ? mSearchHistory
        .filter((item,index) => (index !== aIndex)) : []
      LookupApiUpdateSearchHistory(mSecurityToken.current, SearchHistory)
        .then(() => LookupApiGetSearchHistory(mSecurityToken.current))
        .then((searchHistory) => { SetSearchHistory(searchHistory)})
    } 
    catch (ex) 
    {    
      console.log("RemoveFromSearchHistory failed",ex)
    }
  }

  const AddToSpeedDial = async (aNumber, aName, aApplication, aScripts) => 
  {
    try 
    {  
      let SpeedDials = (mSpeedDials === undefined) || (mSpeedDials === null) ? [] : mSpeedDials
      SpeedDials = [...SpeedDials,...[{ Number: aNumber, Name:aName, Application:aApplication, Scripts:aScripts}]]
      LookupApiUpdateSpeedDials(mSecurityToken.current, SpeedDials)
        .then(() => LookupApiGetSpeedDials(mSecurityToken.current))
        .then((speedDials) => { SetSpeedDials(speedDials)})
    } 
    catch (ex) 
    {    
      console.log("AddToSpeedDial failed",ex);
    }
  }

  const SetNewestCallEventLookupIndex = (index) => 
  {
    localStorage.setItem("newestcallevent_lookupindex", index)

    try
    {
      const CallEventString = localStorage.getItem("newestcallevent")
      if (!IsEmptyString(CallEventString))
      {
        const CallEvent = JSON.parse(CallEventString)
        if ((CallEvent.Recognitions ?? []).length > index)
        {            
          SetHistoryCallRecognition(CallEvent.CallId,GetRecognitionFieldTypeValue("Name",CallEvent.Recognitions[index].RecognitionFieldTypes,CallEvent.Recognitions[index].RecognitionFields),CallEvent.Recognitions[index].Application,CallEvent.Recognitions[index].Scripts)
        }
      }
    }
    catch { }

    SetResetter(uuidv4())
  }

  const SetHistoryCallRecognition = (callid,name,application,scripts) =>
  {  
    console.log("SetHistoryCallRecognition",callid,name,application,scripts)
    let HistoryCallRecognition = GetHistoryCallRecognition()
    HistoryCallRecognition[callid] = {"name":name,"application":application,"scripts":scripts}
    localStorage.setItem("HistoryCallRecognition", JSON.stringify(HistoryCallRecognition))

    UpdateCallHistoryWithRecognitionCache(HistoryCallRecognition)

    SetResetter(uuidv4())
    SetRepainter(!mRepainter)
    const timeout = setTimeout(() => {
      mBroadcastChannel.postMessage("calllistchanged")
    }, 500)
  }

  const ApplyPhoneSettings = (data) => {
    SetPhoneSettingsTemplate(data.SettingsTemplate)
    SetPhoneSettings(data.PhoneSettings)
  }

  const ClearCall = () => {
    
    try
    {
      localStorage.removeItem("newestcallevent");

      SetRepainter(!mRepainter)
      const timeout = setTimeout(() => {
        mBroadcastChannel.postMessage("fullrepaint")
      }, 500)
    } 
    catch (ex) 
    {    
      console.log("MakeCall failed", ex);
    }
  } 

  const ChangeLanguageId = (languageid) => {
    SetLanguageId(languageid)
    SetRepainter(!mRepainter)
  } 
  
  const EnableBrowserNotifications = async () =>
  {
    const result = await Notification.requestPermission();
    if (result === 'denied') 
    {
      console.error('The user explicitly denied the permission request.');
      return;
    }
    else if (result === 'granted') 
    {
      console.info('The user accepted the permission request.');
    }

    navigator.serviceWorker.getRegistrations().then((registrations) => {
      const registration = registrations[0]
      if (registration !== undefined)
      {
        registration.pushManager.getSubscription().then((subscription) => {
          if (subscription) 
          {
            console.log('Service worker already subscribed')
            const key = subscription.getKey("p256dh")
            const encodedKey = key ? btoa(String.fromCharCode.apply(null, new Uint8Array(key))) : null;
            const auth = subscription.getKey("auth")
            const encodedAuth = auth ? btoa(String.fromCharCode.apply(null, new Uint8Array(auth))) : null;
            PushApiSetBrowserNotificationSetting(mSecurityToken.current, true).then(() => PushApiAddBrowserNotification(mSecurityToken.current, "", subscription.endpoint, encodedKey, encodedAuth, "").then(() => SetBrowserNotificationsActive(true))) 
          }
          else
          {
            registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: UrlBase64ToUint8Array(cPushPublicVapidKey) })
              .then((subscription) => {              
                console.log('Service worker subscribe succeeded:', subscription)
                const key = subscription.getKey("p256dh")
                const encodedKey = key ? btoa(String.fromCharCode.apply(null, new Uint8Array(key))) : null;
                const auth = subscription.getKey("auth")
                const encodedAuth = auth ? btoa(String.fromCharCode.apply(null, new Uint8Array(auth))) : null;
                PushApiSetBrowserNotificationSetting(mSecurityToken.current, true).then(() => PushApiAddBrowserNotification(mSecurityToken.current, "", subscription.endpoint, encodedKey, encodedAuth, "").then(() => SetBrowserNotificationsActive(true)))    
              })
          }
        })
      }
    })
  }

  const GetVisualMode = () => {
    return (window.innerWidth < 740) ? "MobileMode" : (window.innerWidth < 930 ? "CompactModeNoHistorySearch" : (window.innerWidth < 1260 ? "CompactMode" : "FullMode"))
  }

  const ValidateTokenExpiration = async (token) => 
  {
    const Now = new Date()
    const DecodedToken = jwtDecode(token)

    //Check token expiration
    if (DecodedToken.exp * 1000 > (Now.getTime() + (7 * 24 * 60 * 60 * 1000))) 
    {
      //token is valid and will remain valid for at least 7 days
      return token
    }
    else if (DecodedToken.exp * 1000 > Now.getTime()) 
    {    
      //token will expire within 7 days
      try
      {
        const data = await SigninApiTokenRefresh(token)
        if (data && (data !== undefined) && data.SecurityToken && (data.SecurityToken !== undefined))
        {
          return data.SecurityToken
        }
        else
        {
          return token
        }
      }
      catch
      { 
        //Token is still valid
        return token
      } 
    }
    else
    {
      //Token has expired. Sign out user so you will no longer get events.
      return ""
    }
  } 

  const UpdateSecurityToken = async () => {
    if (props.SigninMethod === "gntel")
    {
      try
      {
        const SecurityToken = await GntelRefreshTokenWhenNecessary()
        if (IsEmptyString(SecurityToken))
        {
          mSecurityToken.current = ""
        }
        else if (SecurityToken !== mSecurityToken.current)
        {
          localStorage.setItem("securitytoken",SecurityToken)
          mSecurityToken.current = SecurityToken
        }
      }
      catch {
        mSecurityToken.current = ""
      } 
    } 
    else //gntel tokens expire much faster than cloudcti tokens, so no need to do both
    {
      mSecurityToken.current = await ValidateTokenExpiration(mSecurityToken.current)
      localStorage.setItem("securitytoken", mSecurityToken.current)
    }
  }

  const GntelRefreshTokenWhenNecessary = async () => {
    try
    {
      if (GntelTokenIsExpiredOrExpiresSoon())
      {
        console.log("Gntel tokens are expired or expire soon. Getting new tokens")
        if (await GntelRefreshTokens())
        {
          const result = await SigninApiGetTokenGntel(mSecurityTokenData.Email, GntelAccessToken(), GntelRefreshToken(), GntelJsonRpcApiUrl())
          if (result.SecurityToken)
          {
            localStorage.setItem("securitytoken", result.SecurityToken)
            return result.SecurityToken
          }
          else
          {
            return ""
          }
        }
        else
        {
          return ""
        }
      }
      else
      {
        return mSecurityToken.current
      }
    }
    catch (ex) {
      console.log("GntelRefreshTokenWhenNecessary error",ex)
      return ""
    }  
  }

  const MakeCall = async (aNumber, aName, aApplication, aScripts) => {
    try 
    {  
      await UpdateSecurityToken()
      if (IsEmptyString(mSecurityToken.current))
      {
        SignOut()
        return
      }

      //Make up a callid
      const CallId = uuidv4()

      //Do the actual makecall
      PhoneApiMakeCall(mSecurityToken.current, aNumber, true)

      if ((aName === null) && (aApplication === null) && (aScripts === null))
      {
        const result = await LookupApiLookupNumber(mSecurityToken.current, aNumber, CallId, "")

        let FoundRecognition = false
        result.data.Matches.map((match) => 
          {
            if (match.IntegrationGuid && !FoundRecognition)
            {
              const NameIdentifier = GetPropertyByValue(match.RecognitionFieldTypes, "Name")
              if (!IsEmptyString(NameIdentifier))
              {
                aName = match.RecognitionFields[NameIdentifier]
              }
              aApplication = match.ApplicationName
              aScripts = match.Scripts
              FoundRecognition = true;
            }
          })
      }

      //Save the call online
      PushApiCallHistoryAddOutboundCall(mSecurityToken.current, CallId, aNumber, aName, aApplication, aScripts, false)

      //Save the call locally in the call history 
      let CallHistory = JSON.parse(localStorage.getItem("callhistory"))
      CallHistory = [...[{ CallId: CallId, Number: aNumber, Name: aName, Application: aApplication, Scripts: aScripts, Incoming: false, DateTime: NowInDateTimeFormat() }],...CallHistory]
      localStorage.setItem("callhistory",JSON.stringify(CallHistory))

      SetRepainter(!mRepainter)
      const timeout = setTimeout(() => {
        mBroadcastChannel.postMessage("calllistchanged")
      }, 500)
    } 
    catch (ex) 
    {    
      console.log("MakeCall failed", ex);
    }
  }  

  const MobileModeWindowChange = (aWindow) =>
    {
      SetMobileModeWindow(aWindow)
      localStorage.setItem("MobileModeWindow",aWindow)
  }

  const OpenPhoneSettings = () =>
  {
    SetShowModalUserPhoneSettings(true)
  }

  const OpenWebConfigurator = () => 
  {
    window.open((props.ProductName === "Voxone" ? "https://webconfig.voxone.co.uk" : "https://webconfigurator.cloudcti.nl") + "?handover_token=" + mSecurityToken.current)
  } 

  const ProcessPhoneApiSubscribeResult = (result) => {
    console.log("ProcessPhoneApiSubscribeResult",result)
    SetRepainter(!mRepainter)
    if ((result.status !== undefined) && (result.status === 200))
    {
      SetPhoneApiError("")
    }
    else
    {
      let Error = result.message
      if (result.response && result.response.data && result.response.data.Exception && result.response.data.Exception.Message)
      {        
        try { Error = result.response.data.Exception.Message } catch {}
      }
      else if (result.response && !IsEmptyString(result.response.data))
      {
        try { Error = result.response.data } catch {}
      }
      SetPhoneApiError(Error)
    }

    PhoneApiGetPhoneSettings(mSecurityToken.current).then((result) => ProcessPhoneApiGetSettingsResult(result)) 
  }

  const ProcessPhoneApiGetSettingsResult = (result) => {
    if ((result.status !== undefined) && (result.status === 200))
    {
      //console.log("ProcessPhoneApiGetSettingsResult ApplyPhoneSettings")
      //console.log(result.data)
      ApplyPhoneSettings(result.data)
    }
    else
    {
      let Error = result.message
      if (result.response && result.response.data && result.response.data.Exception && result.response.data.Exception.Message)
      {        
        try { Error = result.response.data.Exception.Message } catch {}
      }
      else if (result.response && !IsEmptyString(result.response.data))
      {
        try { Error = result.response.data } catch {}
      }
      SetPhoneApiError(Error)
    }
  }

  const ProcessPushApiGetBrowserNotificationSettingResult = (result) => {
    if (IsEmptyString(props.Embedded) && (result.status === 200) && (result.data.BrowserNotificationsEnabled))
    { 
      EnableBrowserNotifications().then(() => SetBrowserNotificationsActive(true))
    }
    else
    {
      SetBrowserNotificationsActive(false)
    }
  }

  const ProcessPhoneApiSetSettingsResult = (result) => {
    console.log("ProcessPhoneApiSetSettingsResult",result)
    if ((result.status !== undefined) && (result.status === 200))
    {
      PhoneApiSubscribe(mSecurityToken.current).then((subresult) => ProcessPhoneApiSubscribeResult(subresult))
    }
    else
    {
      let Error = result.message
      if (result.response && result.response.data && result.response.data.Exception && result.response.data.Exception.Message)
      {        
        try { Error = result.response.data.Exception.Message } catch {}
      }
      else if (result.response && !IsEmptyString(result.response.data))
      {
        try { Error = result.response.data } catch {}
      }
      SetPhoneApiError(Error)
    }    
  }

  const RecognitionCacheAdd = (aNumber,aRecognition) =>
  {
    let RecognitionCache = {...mRecognitionCache}
    RecognitionCache[aNumber] = aRecognition
    localStorage.setItem("recognitioncache",JSON.stringify(RecognitionCache))
    SetRecognitionCache(RecognitionCache)
  }

  const RecognitionCacheAddMultiple = (aMultiRecognitionResponse,aNumbersQueried,aRecognitionCache) => 
  {
    console.log("aMultiRecognitionResponse",aMultiRecognitionResponse.data.Results)
    console.log("aNumbersQueried",aNumbersQueried)
    //Add the recognition to the cache
    let RecognitionCache = {...aRecognitionCache}
    aMultiRecognitionResponse.data.Results.map((item) => 
      {
        aNumbersQueried = aNumbersQueried.filter((number) => number !== item.Number)
        RecognitionCache[item.Number] = item
      })

    console.log("Numbers with no recognition","")
    aNumbersQueried.map((number) => 
      {
        RecognitionCache[number] = null
      })

    UpdateCallHistoryWithRecognitionCache(RecognitionCache)

    //Save the recognition cache
    localStorage.setItem("recognitioncache",JSON.stringify(RecognitionCache))
    SetRecognitionCache(RecognitionCache)    
    SetRecognitionCacheQueryingNumbers([])
  } 

  const RecognitionCacheClean = (recognitionCache,historyResult) =>
  {
    let RecognitionCache = {}
    Object.keys(recognitionCache).map(cachenumber => {
      if (historyResult.filter((key) => key.Number === cachenumber).length > 0)
      {
        RecognitionCache[cachenumber] = recognitionCache[cachenumber]
      }
    })
    localStorage.setItem("recognitioncache",JSON.stringify(RecognitionCache))
    SetRecognitionCache(RecognitionCache)
    return RecognitionCache
  }
  
  const RemoveFromCallHistory = async (aCallId) => 
  {
    try 
    {  
        console.log("RemoveFromCallHistory", aCallId);
        await PushApiCallHistoryDeleteCall(mSecurityToken.current, aCallId)

        let CallHistory = GetCallHistoryFromLocalStorage()
        CallHistory = CallHistory.filter(call => (call.CallId !== aCallId))
        localStorage.setItem("callhistory",JSON.stringify(CallHistory))

        SetRepainter(!mRepainter)
        const timeout = setTimeout(() => {
          mBroadcastChannel.postMessage("calllistchanged")
        }, 500)
    } 
    catch (ex) 
    {    
        console.log("RemoveFromCallHistory failed", ex);
    }    
  }
    
  const RemoveFromSpeedDial = async (aNumber) => 
  {
    try 
    {  
      const SpeedDials = mSpeedDials.filter((item,index) => item.Number !== aNumber)
      LookupApiUpdateSpeedDials(mSecurityToken.current, SpeedDials)
        .then(() => LookupApiGetSpeedDials(mSecurityToken.current))
        .then((speedDials) => { SetSpeedDials(speedDials)})
    } 
    catch (ex) 
    {    
      let error = ex;
      console.log("RemoveFromSpeedDial failed: " + error);
    }
  } 

  const ShouldUseGeneralLookupNumber  = () => {
    return IsEmptyString(props.Embedded)
  }

  const SignOut = () => {

    //Remove browser notifications for this service worker on signout
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      if (registrations.length > 0) {
        const registration = registrations[0]
        registration.pushManager.getSubscription().then((subscription) => {
          console.log("SignOut",subscription)
          if (subscription) 
          {
            const key = subscription.getKey("p256dh")
            const encodedKey = key ? btoa(String.fromCharCode.apply(null, new Uint8Array(key))) : null;
            const auth = subscription.getKey("auth")
            const encodedAuth = auth ? btoa(String.fromCharCode.apply(null, new Uint8Array(auth))) : null;
            PushApiDeleteBrowserNotification(mSecurityToken.current, subscription.endpoint, encodedKey, encodedAuth) 
          }
        })
      }
      else
        console.warn("No service worker registrations found on signout")
    })
    RemoveServiceWorker()

    localStorage.clear()
    Cookies.remove('webclient', { domain: '.cloudcti.nl' })
    mBroadcastChannel.postMessage("signoutmessage"); // Tell other tabs to load the redirect url as well, signs them out
    window.location.href = props.RedirectUrl
  }  

  const OnSignalRError = (error) => {
    console.log("SignalR error: " + error)
  }  

  const OnSignalREvent = async (event) => 
  {
    let CallEvent
    try
    {
      CallEvent = JSON.parse(event)
      console.log("New call event", CallEvent)
    }
    catch 
    { 
      console.log("Could not parse call event", event)
      return
    }
    
    if (!IsEmptyString(CallEvent.CallId) && !IsEmptyString(CallEvent.State))
    {
      //Determine whether or not this is the primary tab
      //The primary tab is the first tab to process an event (callid + state)
      //New tabs that don't get this event always have PrimaryTab=false because they never have to automatically do something
      //An event with the same callid and state will always return PrimaryTab as false
      const EventId = CallEvent.CallId + "." + CallEvent.State
      const PreviousEventId = localStorage.getItem("primarytab_lasteventid") //Use the localstorage as a mutex
      localStorage.setItem("primarytab_lasteventid", EventId) 
      const PrimaryTab = PreviousEventId !== EventId
      SetPrimaryTab(PrimaryTab)

      if (PrimaryTab)
      {      
        console.log("Call event received on primary tab", CallEvent);

        const IsInbound = CallEvent.Direction.toLowerCase() === "inbound";

        //Retrieve the previous call event from localstorage
        let LastCallEvent = null
        try
        {
          const LastCallString = localStorage.getItem("newestcallevent")
          LastCallEvent = !IsEmptyString(LastCallString) ? JSON.parse(LastCallString) : null
        }
        catch {}
        const LastCallId = (LastCallEvent !== null) ? LastCallEvent.CallId : ""

        if (((CallEvent.State === "ended") || (CallEvent.State === "")) && IsInbound)
        {
          //Updates call history
          await ProcessCallEventUpdateHistory(CallEvent, LastCallEvent, IsInbound)
        }

        //Add starttime, answertime and endtime to the callevent
        if ((LastCallEvent == null) || IsEmptyString(LastCallEvent.CallId) || (LastCallEvent.CallId !== CallEvent.CallId))
        {
          //New call
          CallEvent.StartTime = Date.now()
          CallEvent.AnswerTime = (CallEvent.State.toLowerCase() === "answered") ? Date.now() : null
          CallEvent.EndTime = (CallEvent.State.toLowerCase() === "ended") ? Date.now() : null
        }
        else
        {
          //New event for same call
          CallEvent.StartTime = LastCallEvent.StartTime
          CallEvent.AnswerTime = (LastCallEvent.AnswerTime === null) && (CallEvent.State.toLowerCase() === "answered") ? Date.now() : LastCallEvent.AnswerTime
          CallEvent.EndTime = (LastCallEvent.EndTime === null) && (CallEvent.State.toLowerCase() === "ended") ? Date.now() : LastCallEvent.EndTime      
        }     
        
        if (LastCallId !== CallEvent.CallId)
        {
          CallEvent.Recognitions = CallEvent.Recognitions ?? []
          CallEvent.CallfieldScripts = CallEvent.CallfieldScripts ?? []
          if (ShouldUseGeneralLookupNumber())
          {
            let Recognitions = []
            let CallfieldScripts = []
            try
            {
              const result = await LookupApiLookupNumber(mSecurityToken.current, CallEvent.Direction.toLowerCase() === "inbound" ? CallEvent.CallerNumber : CallEvent.CalledNumber, CallEvent.CallId ?? "", CallEvent.CallRecordingId ?? "")

              result.data.Matches.map((match) => 
              {
                if (match.IntegrationGuid)
                {
                  Recognitions.push(match)
                }
                else
                {
                  CallfieldScripts = match.Scripts
                }
              })
            }
            catch { }
            CallEvent.Recognitions = Recognitions
            CallEvent.CallfieldScripts = CallfieldScripts
            
            if ((CallEvent.Recognitions ?? []).length > 0)
            {
              let Scripts = []   

              CallEvent.Recognitions[0].Scripts.forEach((script) => 
                { 
                  if (!script.ScreenpopUrl.toLowerCase().startsWith("screenpop:"))
                  {
                    script.CallfieldScript = false; 
                    Scripts.push(script);
                  } 
                })
              if (CallEvent.CallfieldScripts != null)
              {
                  CallEvent.CallfieldScripts.forEach((script) => 
                    { 
                      if (!script.ScreenpopUrl.toLowerCase().startsWith("screenpop:"))
                      {
                        script.CallfieldScript = true; 
                        Scripts.push(script); 
                      }
                    })
              }
              
              SetHistoryCallRecognition(CallEvent.CallId,GetRecognitionFieldTypeValue("Name",CallEvent.Recognitions[0].RecognitionFieldTypes,CallEvent.Recognitions[0].RecognitionFields),CallEvent.Recognitions[0].Application,Scripts)
            }
          }

          localStorage.setItem("newestcallevent_lookupindex", 0);
        }
        else
        {
          CallEvent.Recognitions = LastCallEvent.Recognitions
          CallEvent.CallfieldScripts = LastCallEvent.CallfieldScripts
        }

        localStorage.setItem("newestcallevent", JSON.stringify(CallEvent));
      }   
      SetLastCallEventId(EventId)
    }
  }

  const ProcessCallEventUpdateHistory = async (aCallEvent, aLastCallEvent, isInbound) => 
  {
    let Name = ""
    let ApplicationName = ""
    let Scripts = []
    try
    {
      if (aLastCallEvent.CallId === aCallEvent.CallId)
      {
        let FoundRecognition = false
        aLastCallEvent.Recognitions.map((match) => 
          {
            if (match.IntegrationGuid && !FoundRecognition)
            {
              const NameIdentifier = GetPropertyByValue(match.RecognitionFieldTypes, "Name")
              if (!IsEmptyString(NameIdentifier))
              {
                Name = match.RecognitionFields[NameIdentifier]
              }
              ApplicationName = match.ApplicationName
              Scripts = match.Scripts
              FoundRecognition = true;
            }
          })

        aLastCallEvent.Recognitions.map((match) => 
          {
            if (!match.IntegrationGuid)
            {
              Scripts = [...Scripts,...match.Scripts]
            }
          })
      }
      else if (ShouldUseGeneralLookupNumber())
      {
        const LookupResult = await LookupApiLookupNumber(mSecurityToken.current, aCallEvent.CallerNumber, aCallEvent.CallId ?? "", aCallEvent.CallRecordingId ?? "")
    
        let FoundRecognition = false
        LookupResult.data.Matches.map((match) => 
          {
            if (match.IntegrationGuid && !FoundRecognition)
            {
              const NameIdentifier = GetPropertyByValue(match.RecognitionFieldTypes, "Name")
              if (!IsEmptyString(NameIdentifier))
              {
                Name = match.RecognitionFields[NameIdentifier]
              }
              ApplicationName = match.ApplicationName
              Scripts = match.Scripts
              FoundRecognition = true;
            }
          })

          LookupResult.data.Matches.map((match) => 
          {
            if (!match.IntegrationGuid)
            {
              Scripts = [...Scripts,...match.Scripts]
            }
          })
      }
    }
    catch (ex)
    { 
      console.log("OnSignalREvent error", ex)
    }

    let CallHistory = []
    CallHistory.push({ Id: aCallEvent.CallId, CallId: aCallEvent.CallId, Number: isInbound ? aCallEvent.CallerNumber : aCallEvent.CalledNumber, Name: Name, Application: ApplicationName, Scripts: Scripts, Incoming: isInbound, DateTime: NowInDateTimeFormat() })   
    const OldCallHistoryString = localStorage.getItem("callhistory")
    if (!IsEmptyString(OldCallHistoryString))
    {
      const OldCallHistory = JSON.parse(OldCallHistoryString)
      OldCallHistory.map((call) => CallHistory.push(call))
    }
    localStorage.setItem("callhistory",JSON.stringify(CallHistory))

    SetRepainter(!mRepainter)
    setTimeout(() => {
      mBroadcastChannel.postMessage("calllistchanged")
    }, 500) 
  }

  const UpdatePhoneSettings = () => {
    PhoneApiSetPhoneSettings(mSecurityToken.current,mPhoneSettings).then((result) => ProcessPhoneApiSetSettingsResult(result))  
  }

  const PhoneSubscribe = () => {
    PhoneApiSubscribe(mSecurityToken.current).then((result) => ProcessPhoneApiSubscribeResult(result))
  }

  //----> HTML

  const ReturnNotificationSection = () => {

    let NewestCallEvent = null
    try
    {
      const NewestCallString = localStorage.getItem("newestcallevent");
      NewestCallEvent = !IsEmptyString(NewestCallString) ? JSON.parse(NewestCallString) : null
      if (NewestCallEvent != null)
      {
          NewestCallEvent.Recognitions = NewestCallEvent.Recognitions ?? []
          NewestCallEvent.CallfieldScripts = NewestCallEvent.CallfieldScripts ?? []
      }
    } catch {}

    let NewestCallEventLookupIndex = 0
    try
    {
      NewestCallEventLookupIndex = parseInt(localStorage.getItem("newestcallevent_lookupindex"),10)
    } catch {}

    return <div className={!IsEmptyString(mPhoneApiError) ? "notification notificationerror" : "notification"}>
        {!IsEmptyString(mPhoneApiError) ? <><div>{Translate("Failed to subscribe for phone events")}</div>{Translate("Error") + ": " + mPhoneApiError}<br/><div className="mainoverride hyperlink" onClick={() => OpenPhoneSettings()}>Open phone settings</div></> : 
          (NewestCallEvent === null ? Translate("No incoming call information available") : <CallNotification Brand={props.Brand} Call={NewestCallEvent} LookupResultIndex={NewestCallEventLookupIndex} OnSetLookupResultIndex={(index) => SetNewestCallEventLookupIndex(index)}/>)
        }
    </div>  
  }

  const ReturnMobileMode = () => {
    return <table width="100%" height="100%">        
    <tbody>
    <tr height="24">
        <td width="33%" className={mMobileModeWindow === "Search" ? "mobilemodewindow mobilemodewindowselected" : "mobilemodewindow"} onClick={() => MobileModeWindowChange("Search")}><span className="up3px">{Translate("Search").toUpperCase()}</span></td>
        <td width="34%" className={mMobileModeWindow === "History" ? "mobilemodewindow mobilemodewindowselected" : "mobilemodewindow"} onClick={() => MobileModeWindowChange("History")}><span className="up3px">{Translate("History").toUpperCase()}</span></td>
        <td width="33%" className={mMobileModeWindow === "Speed dial" ? "mobilemodewindow mobilemodewindowselected" : "mobilemodewindow"} onClick={() => MobileModeWindowChange("Speed dial")}><span className="up3px">{Translate("Speed dial").toUpperCase()}</span></td>
    </tr>
    <tr height="*">

        {mMobileModeWindow === "Search" ? 

            <td colSpan="3" valign="top">
              
              <table width="100%" height="100%" cellPadding="0" cellSpacing="0">
              <tbody>
                <tr height="30">
                  <td valign="center" align="left" className="padded10">
                    {ReturnNotificationSection()}
                  </td>
                </tr>   
                <tr height="*">
                  <td valign="top" align="left">
                    <div className="scrollbary">
                        <SearchArea 
                          SecurityToken={mSecurityToken.current}
                          SearchHistory={mSearchHistory}
                          Brand={props.Brand}
                          OnMakeCall={(number, name, application, scripts) => MakeCall(number, name, application, scripts)}
                          OnAddToSearchHistory={(number, name, organization, application, scripts) => AddToSearchHistory(number, name, organization, application, scripts)}
                          OnRemoveFromSearchHistory={(index) => RemoveFromSearchHistory(index)}
                        />
                    </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>

            : (mMobileModeWindow === "History" ? 

            <td colSpan="3" align="left" valign="top">
              <CallHistory 
                Calls={GetCallHistory()} 
                QueryingRecognitionNumbers={mRecognitionCacheQueryingNumbers}
                SpeedDials={((mSpeedDials === undefined) || (mSpeedDials === null) || !mSpeedDials) ? [] : mSpeedDials} 
                ShowHeader={false} 
                AddSearch={true}
                Brand={props.Brand}
                OnMakeCall={(number, name, application, scripts) => MakeCall(number, name, application, scripts)} 
                OnAddToSpeedDial={(number, name, application, scripts) => AddToSpeedDial(number, name, application, scripts)}
                OnRemoveFromSpeedDial={(number) => RemoveFromSpeedDial(number)} 
                OnRemoveFromCallHistory={(callid) => RemoveFromCallHistory(callid)}
              />
            </td>

            :

            <td colSpan="3" align="left" valign="top">                      
              <SpeedDials 
                SpeedDials={((mSpeedDials === undefined) || (mSpeedDials === null) || !mSpeedDials) ? [] : mSpeedDials} 
                ShowHeader={false} 
                Brand={props.Brand}
                OnMakeCall={(number, name, application, scripts) => MakeCall(number, name, application, scripts)} 
                OnRemoveFromSpeedDial={(number) => RemoveFromSpeedDial(number)}
              />
            </td>)
          }
    </tr>
    </tbody>
    </table>
  }

  const ReturnCompactMode = (aAddHistorySearch) => {
    return <table width="100%" height="100%" className="main">        
    <tbody>
    <tr>
        <td width="366" valign="top">

        <table width="100%" height="100%" cellPadding="0" cellSpacing="0">
          <tbody>
            <tr height="30">
              <td valign="center" align="left" className="padded10">
                {ReturnNotificationSection()} 
              </td>
            </tr>   
            <tr height="*">
              <td valign="top" align="left">
                <SearchArea 
                  SecurityToken={mSecurityToken.current}
                  SearchHistory={mSearchHistory}
                  Brand={props.Brand}
                  OnMakeCall={(number, name, application, scripts) => MakeCall(number, name, application, scripts)}
                  OnAddToSearchHistory={(number, name, organization, application, scripts) => AddToSearchHistory(number, name, organization, application, scripts)}
                  OnRemoveFromSearchHistory={(index) => RemoveFromSearchHistory(index)}
                />
              </td>
            </tr>
          </tbody>
        </table>

        </td>
        <td width="*" valign="top" className="mainborderleft mainborderright">
          <SpeedDials 
            SpeedDials={((mSpeedDials === undefined) || (mSpeedDials === null) || !mSpeedDials) ? [] : mSpeedDials} 
            ShowHeader={true} 
            Brand={props.Brand}
            OnMakeCall={(number, name, application, scripts) => MakeCall(number, name, application, scripts, true, false)} 
            OnRemoveFromSpeedDial={(number) => RemoveFromSpeedDial(number)}
          />
          <CallHistory 
            Calls={GetCallHistory()} 
            QueryingRecognitionNumbers={mRecognitionCacheQueryingNumbers}
            SpeedDials={((mSpeedDials === undefined) || (mSpeedDials === null) || !mSpeedDials) ? [] : mSpeedDials} 
            ShowHeader={true} 
            AddSearch={aAddHistorySearch}
            Brand={props.Brand}
            OnMakeCall={(number) => MakeCall(number, null, null, null)} 
            OnAddToSpeedDial={(number, name, application, scripts) => AddToSpeedDial(number, name, application, scripts)}
            OnRemoveFromSpeedDial={(number) => RemoveFromSpeedDial(number)} 
            OnRemoveFromCallHistory={(callid) => RemoveFromCallHistory(callid)}
          />
        </td>
    </tr>
    </tbody>
    </table>    
  }

  const ReturnFullMode = () => {
    return <table width="100%" height="100%">        
      <tbody>
      <tr>        
          <td width="366" valign="top">

          <table width="100%" height="100%" cellPadding="0" cellSpacing="0">
            <tbody>
              <tr height="30">
                <td valign="center" align="left" className="padded10">
                  {ReturnNotificationSection()}  
                </td>
              </tr>   
              <tr height="*">
                <td valign="top" align="left">
                  <SearchArea 
                    SecurityToken={mSecurityToken.current}
                    SearchHistory={mSearchHistory}
                    Brand={props.Brand}
                    OnMakeCall={(number, name, application, scripts) => MakeCall(number, name, application, scripts)}
                    OnAddToSearchHistory={(number, name, organization, application, scripts) => AddToSearchHistory(number, name, organization, application, scripts)}
                    OnRemoveFromSearchHistory={(index) => RemoveFromSearchHistory(index)}
                  />
                </td>
              </tr>
            </tbody>
          </table>

          </td>
          <td width="*" valign="top" className="mainborderleft mainborderright">
            <CallHistory 
              Calls={GetCallHistory()} 
              QueryingRecognitionNumbers={mRecognitionCacheQueryingNumbers}
              SpeedDials={((mSpeedDials === undefined) || (mSpeedDials === null) || !mSpeedDials) ? [] : mSpeedDials} 
              ShowHeader={true} 
              AddSearch={true}
              Brand={props.Brand}
              OnMakeCall={(number) => MakeCall(number, null, null, null)} 
              OnAddToSpeedDial={(number, name, application, scripts) => AddToSpeedDial(number, name, application, scripts)}
              OnRemoveFromSpeedDial={(number) => RemoveFromSpeedDial(number)} 
              OnRemoveFromCallHistory={(callid) => RemoveFromCallHistory(callid)}
            />
          </td>
          <td width="339" valign="top">
            <SpeedDials 
              SpeedDials={((mSpeedDials === undefined) || (mSpeedDials === null) || !mSpeedDials) ? [] : mSpeedDials} 
              ShowHeader={true} 
              Brand={props.Brand}
              OnMakeCall={(number, name, application, scripts) => MakeCall(number, name, application, scripts, true, false)} 
              OnRemoveFromSpeedDial={(number) => RemoveFromSpeedDial(number)}
            />
          </td>
      </tr>
      </tbody>
      </table>   
  }

  const Icon = props.Brand === "summa" ? SummaPng :
    props.Brand === "enreach" ? EnreachPng :
      !IsEmptyString(mSecurityTokenData.BrandLogoUrl) ? mSecurityTokenData.BrandLogoUrl : CloudCTIPng
  
  return (
    <span className="main">

      <table className="main" height="100%" width="100%" cellPadding={0}>        
      <tbody>
        <tr height="0">
          <td width="100"/>
          <td width="*"/>
          <td width="100"/>
        </tr>
        <tr height="25" className="topbar"> 
          <td></td>
          <td align="center" valign="center">    
              {mSecurityTokenData.Email != null ? 
                <img src={Icon} className="cloudctiicon"/> : 
                <img src={LogoEmptyPng} className="cloudctiicon"/>}  
          </td>          
          <td align="right" valign="center">      
              {SingleAccountButton() ?
              <>
                <Popup trigger={<img src={AccountPng} style={{ paddingRight: "20px", cursor: "pointer"}}/>} arrow={true} closeOnDocumentClick={true} keepTooltipInside={true} contentStyle={{ backgroundColor: "white", color: "#293239", width: "300px", padding: "0px", border: "1px #e8eaec solid"}}>
                    {(close) => (
                    <>
                      <div className="accountpopupmenuheader">
                        <table>
                        <tbody>
                          <tr>
                            <td><img src={AccountPng} style={{paddingTop: "2px"}}/></td>
                            <td width="10"/>
                            <td>
                              <b>{mSecurityTokenData.Email}</b><br/>
                              <span style={{ color: "#777777", fontSize: "13px"}}>{mSecurityTokenData.TenantName}</span></td>
                          </tr>
                        </tbody>
                        </table>
                      </div> 
                      <hr className="accountpopupmenudivider"/>
                      <div className="accountpopupmenuitem" onClick={() => { close(); SetShowModalUserSettings(true)}}>{Translate("Settings")}</div>    
                      <hr className="accountpopupmenudivider"/>
                      <div className="accountpopupmenuitem"onClick={() => { close(); window.open("https://downloadcloudcti.blob.core.windows.net/files/Website/Terms%20of%20Delivery%20CloudCTI-2015-1%20EN.pdf") }}>{Translate("Terms and conditions")}</div>    
                      <div className="accountpopupmenuitem"onClick={() => { close(); window.open("https://cloudcti.nl/privacy-policy") }}>{Translate("Privacy statement")}</div>    
                      <hr className="accountpopupmenudivider"/>
                      <div className="accountpopupmenuitem" onClick={() => { close(); SignOut()}}>{Translate("Sign out")}</div>  
                      <div className="accountpopupmenubottom"/>
                    </>   
                )}     
                </Popup>            
              </> :
              <>     
                <Button variant="outline-info" className="primarybutton link" title={Translate("Settings")} onClick={() => SetShowModalUserSettings(true)}><img src={SettingsUserPng}/></Button>
                <Button variant="outline-info" className="primarybutton link" title={Translate("Sign out")} onClick={() => SignOut()}><img src={SignOutPng}/></Button>
              </>}
              &nbsp;
          </td>   
      </tr>
        <tr height="*"> 
            <td colSpan="3" width="*" align="center" valign="top">
              {SelectPageContent()}
            </td>
        </tr>
        </tbody>
      </table>

      {(mShowModalUserSettings || mShowModalUserPhoneSettings) ? <UserSettingsModal SecurityToken={mSecurityToken.current} Email={mSecurityTokenData.Email} UserName={mSecurityTokenData.UserName} TenantName={mSecurityTokenData.TenantName} LicenceName={mSecurityTokenData.LicenceName} LicencePlatforms={mSecurityTokenData.LicencePlatforms} LicenceRecognitionAndScreenpop={mSecurityTokenData.UserHasRecognitionAndScreenpopLicense} LanguageId={GetLanguageId()} Integrations={mIntegrations} CallFieldScripts={mCallfieldScripts} UserIsAdmin={mSecurityTokenData.UserIsAdmin ? true : false} BrowserNotificationsActive={mBrowserNotificationsActive} PhoneSettingsTemplate={mPhoneSettingsTemplate} PhoneSettings={mPhoneSettings} PhoneApiError={mPhoneApiError} Embedded={props.Embedded} OnlyShowPhoneSettings={!mShowModalUserSettings} OnHide={() => { SetShowModalUserSettings(false), SetShowModalUserPhoneSettings(false)}} OnSetBrowserNotificationsActive={SetBrowserNotificationsActive} OnUpdatePhoneSettings={() => UpdatePhoneSettings()} OnPhoneSubscribe={() => PhoneSubscribe()} OnUpdateLanguage={ChangeLanguageId} OnTranslate={Translate} OnOpenWebConfigurator={() => OpenWebConfigurator()}/> : <span/>}
    
    </span>
  );

  function SingleAccountButton() {
    return (props.Embedded && (props.Embedded.toLowerCase() === "zendesk"))
  }

  function SelectPageContent() {
    if (props.Embedded) {
      if (props.Embedded.toLowerCase() === "salesforce")
        return <EmbeddedSalesforce
          Calls={GetCallHistory()} 
          SecurityToken={mSecurityToken.current}
          LastCallEventId={mLastCallEventId}
          RecognitionCacheQueryingNumbers={mRecognitionCacheQueryingNumbers}
          SpeedDials={mSpeedDials}
          PrimaryTab={mPrimaryTab}
          Brand={props.Brand}
          OnAddToSpeedDial={AddToSpeedDial}
          OnMakeCall={MakeCall}
          OnRemoveFromCallHistory={RemoveFromCallHistory}
          OnRemoveFromSpeedDial={RemoveFromSpeedDial}
          OnSetHistoryCallRecognition={SetHistoryCallRecognition}/>
      if (props.Embedded.toLowerCase() === "hubspot")
        return <EmbeddedHubspot
          LastCallEventId={mLastCallEventId}
          RecognitionCacheQueryingNumbers={mRecognitionCacheQueryingNumbers}
          SpeedDials={mSpeedDials}
          PrimaryTab={mPrimaryTab}
          Brand={props.Brand}
          OnAddToSpeedDial={AddToSpeedDial}
          OnMakeCall={MakeCall}
          OnRemoveFromCallHistory={RemoveFromCallHistory}
          OnRemoveFromSpeedDial={RemoveFromSpeedDial}/>
      if (props.Embedded.toLowerCase() === "zendesk")
        return <EmbeddedZendesk 
          Calls={GetCallHistory()} 
          SecurityToken={mSecurityToken.current}
          LastCallEventId={mLastCallEventId}
          RecognitionCacheQueryingNumbers={mRecognitionCacheQueryingNumbers}
          SpeedDials={mSpeedDials}
          PrimaryTab={mPrimaryTab}
          Brand={props.Brand}
          OnAddToSpeedDial={AddToSpeedDial}
          OnClearCall={ClearCall}
          OnMakeCall={MakeCall}
          OnRemoveFromCallHistory={RemoveFromCallHistory}
          OnRemoveFromSpeedDial={RemoveFromSpeedDial}
          OnSetHistoryCallRecognition={SetHistoryCallRecognition}/>
      if (props.Embedded.toLowerCase() === "zoho")
        return <EmbeddedZoho
          Calls={GetCallHistory()} 
          SecurityToken={mSecurityToken.current}
          PhoneApiError={mPhoneApiError}
          LastCallEventId={mLastCallEventId}
          Brand={props.Brand}
          RecognitionCacheQueryingNumbers={mRecognitionCacheQueryingNumbers}
          SpeedDials={mSpeedDials}
          PrimaryTab={mPrimaryTab}
          OnAddToSpeedDial={AddToSpeedDial}
          OnClearCall={ClearCall}
          OnMakeCall={MakeCall}
          OnRemoveFromCallHistory={RemoveFromCallHistory}
          OnRemoveFromSpeedDial={RemoveFromSpeedDial}
          OnSetHistoryCallRecognition={SetHistoryCallRecognition}/>
      if (props.Embedded.toLowerCase() === "dynamics")
        return <EmbeddedDynamics
          Calls={GetCallHistory()} 
          SecurityToken={mSecurityToken.current}
          PhoneApiError={mPhoneApiError}
          LastCallEventId={mLastCallEventId}
          Brand={props.Brand}
          RecognitionCacheQueryingNumbers={mRecognitionCacheQueryingNumbers}
          SpeedDials={mSpeedDials}
          PrimaryTab={mPrimaryTab}
          OnAddToSpeedDial={AddToSpeedDial}
          OnClearCall={ClearCall}
          OnMakeCall={MakeCall}
          OnRemoveFromCallHistory={RemoveFromCallHistory}
          OnRemoveFromSpeedDial={RemoveFromSpeedDial}
          OnSetHistoryCallRecognition={SetHistoryCallRecognition}/>
    }
    else {
      if (mVisualMode == "MobileMode")
        return ReturnMobileMode()
      if (mVisualMode == "CompactModeNoHistorySearch")
        return ReturnCompactMode(false)
      if (mVisualMode == "CompactMode")
        return ReturnCompactMode(true)
      return ReturnFullMode()
    }
  }
}

Main.propTypes = {
  ProductName: PropTypes.string,
  SigninMethod: PropTypes.string,
  Domain: PropTypes.string,
  Embedded: PropTypes.string,
  RedirectUrl: PropTypes.string,
  Brand: PropTypes.string,
  Nonce: PropTypes.string
}

export default Main;